import { consoleCanGrantCustomerOwner, type ConsoleRole } from "@flexfreight/core";
import { Button, Dialog, notify, Select, StatusPill, TextField, Toggle, useConfirm } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import type { ConsoleMe } from "../../auth/session";

/* Customers > Add customer, and the Users tab actions (A.40). Staff never see or set a customer's password: people
 * choose their own from a one-time link, sent by email when email is connected, or copied here and passed on. */

export const EMAIL_NOT_CONNECTED = "Email isn't connected, so copy this link and send it yourself.";
export type EmailStatus = { connected: boolean; via: "resend" | "postmark" | null; resendReady?: boolean; message: string | null };
export type OneTimeLink = { url: string; expiresAt: string; kind: "invite" | "reset" };
type AddCustomerOut = { customer: { id: string; name: string }; user: { id: string; email: string }; emailConnected: boolean; emailSent: boolean; link: OneTimeLink | null };
type AddUserOut = { user: { id: string; email: string; pending: boolean }; emailConnected: boolean; emailSent: boolean; link: OneTimeLink | null };
export type CustomerUser = { id: string; name: string | null; email: string; role: string; disabled: boolean; invitePending: boolean; otherCustomers?: number };

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "shipper", label: "Shipper" },
  { value: "billing", label: "Billing" },
  { value: "viewer", label: "Viewer" },
];
/** Owner or admin with an authenticator or text codes: may grant `owner` and copy sign-in links (the API enforces it). */
export const canGrantOwner = (me: Pick<ConsoleMe, "role" | "mfaEnabled">) => consoleCanGrantCustomerOwner(me.role as ConsoleRole, me.mfaEnabled);
/** Roles Add user offers: Owner only to staff who may grant it. */
export const roleOptions = (grantOwner: boolean) => (grantOwner ? ROLES : ROLES.filter((r) => r.value !== "owner"));
export const OWNER_GRANT_HINT = "Only a FlexFreight owner or admin can make someone an owner.";
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const fieldOf = (e: unknown) => (e instanceof ApiError ? ((e.details as { field?: string } | undefined)?.field ?? null) : null);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useEmailStatus() {
  return useQuery({ queryKey: ["console", "email-status"], queryFn: () => api<EmailStatus>("/v1/console/email-status"), staleTime: 30_000 });
}

/** "Email isn't connected" notice, shown wherever an email would otherwise be sent. */
export function EmailNotConnected({ status }: { status: EmailStatus | undefined }) {
  if (!status || status.connected) return null;
  return (
    <p className="ffa-notice ffa-small" role="status">
      <StatusPill tone="warning">{status.resendReady ? "Customer email off" : "Email off"}</StatusPill>
      <span>{status.message ?? EMAIL_NOT_CONNECTED}</span>
      <CustomerEmailFix status={status} />
    </p>
  );
}

/** Why the welcome-email switch is off: the server's reason (Resend on but not chosen for customers, or no email). */
const welcomeOffReason = (status: EmailStatus | undefined) =>
  status?.resendReady ? "Resend is connected, but customer email is switched off." : "Email isn't connected. You'll get a one-time link to send yourself.";

/**
 * Resend already sends staff mail but customer email isn't routed through it: one click turns on "Use Resend for
 * customer emails" (the same switch as in Integrations, Resend). Shown only to staff who may change integrations.
 */
export function CustomerEmailFix({ status }: { status: EmailStatus | undefined }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const turnOn = useMutation({
    mutationFn: async () => {
      const v = await api<{ from: string; enabled: boolean }>("/v1/console/integrations/resend");
      return api<{ customerEmail?: boolean }>("/v1/console/integrations/resend", { method: "PUT", body: { from: v.from, enabled: true, customerEmail: true } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "email-status"] });
      void qc.invalidateQueries({ queryKey: ["console", "integrations", "resend"] });
      notify.success("Customer emails go through Resend", "Welcome emails, password resets and notices now send.");
    },
    onError: (e) => notify.error("Couldn't turn on customer email", message(e)),
  });
  if (!status?.resendReady || !me.capabilities.includes("integrations:manage")) return null;
  return (
    <Button size="sm" variant="primary" loading={turnOn.isPending} onClick={() => turnOn.mutate()}>
      Turn on customer email
    </Button>
  );
}

/** A welcome or reset link, shown once with its expiry, for staff to pass on themselves. */
export function OneTimeLinkDialog({ link, who, connected, emailFailed, onClose }: { link: OneTimeLink; who: string; connected: boolean; emailFailed?: boolean; onClose: () => void }) {
  const what = link.kind === "invite" ? "welcome link" : "password reset link";
  const lead = emailFailed ? "The email didn't send, so copy this link and send it yourself." : connected ? "Copy this link and send it yourself." : EMAIL_NOT_CONNECTED;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${link.kind === "invite" ? "Welcome" : "Password reset"} link for ${who}`}
      description={`${lead} It works once and expires ${when.format(new Date(link.expiresAt))}. It is shown only this once.`}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <div className="ffa-form">
        <StatusPill tone="info">Send it only to {who}: anyone with it can {link.kind === "invite" ? "set the first password" : "reset the password"}</StatusPill>
        <TextField label={`One-time ${what}`} value={link.url} readOnly onFocus={(e) => e.currentTarget.select()} />
        <Button onClick={() => void navigator.clipboard?.writeText(link.url).then(() => notify.success("Link copied"))}>Copy link</Button>
      </div>
    </Dialog>
  );
}

/** Customers > Add customer: a company and its first user (the owner). */
export function AddCustomerDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const status = useEmailStatus();
  const [f, setF] = useState({ companyName: "", phone: "", tier: "", userName: "", userEmail: "", sendWelcome: true });
  const [done, setDone] = useState<AddCustomerOut | null>(null);
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch });
  const add = useMutation({
    mutationFn: () =>
      api<AddCustomerOut>("/v1/console/customers", {
        method: "POST",
        body: { companyName: f.companyName.trim(), phone: f.phone.trim() || null, tier: f.tier.trim() || null, userName: f.userName.trim(), userEmail: f.userEmail.trim(), sendWelcome: f.sendWelcome },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "customers"] });
      if (r.link) setDone(r);
      else {
        notify.success(`${r.customer.name} added`, `Welcome email sent to ${r.user.email}.`);
        onClose();
        void navigate(`/customers/${r.customer.id}?tab=users`);
      }
    },
  });
  if (done?.link) {
    return (
      <OneTimeLinkDialog
        link={done.link}
        who={done.user.email}
        connected={done.emailConnected}
        emailFailed={f.sendWelcome && done.emailConnected && !done.emailSent}
        onClose={() => {
          onClose();
          void navigate(`/customers/${done.customer.id}?tab=users`);
        }}
      />
    );
  }
  const err = add.error;
  const field = fieldOf(err);
  const existing = err instanceof ApiError ? ((err.details as { customers?: { id: string; name: string }[] } | undefined)?.customers ?? []) : [];
  const valid = f.companyName.trim() !== "" && f.userName.trim() !== "" && EMAIL_RE.test(f.userEmail.trim());
  const connected = status.data?.connected ?? false;
  return (
    <Dialog
      open
      onClose={onClose}
      title="Add customer"
      description="Creates the company and its first user as the owner. They choose their own password from a one-time link; you never see it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={add.isPending} onClick={() => add.mutate()}>Add customer</Button>
        </>
      }
    >
      <form
        className="ffa-form"
        aria-label="New customer"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) add.mutate();
        }}
      >
        <TextField label="Company name" value={f.companyName} onChange={(e) => set({ companyName: e.target.value })} maxLength={200} required />
        <TextField label="Company phone" type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} maxLength={40} optional />
        <TextField label="Pricing tier" value={f.tier} onChange={(e) => set({ tier: e.target.value })} maxLength={40} optional hint="A label, as in Signups & approvals. Markup rules are still set in the portal." />
        <TextField label="First user's name" value={f.userName} onChange={(e) => set({ userName: e.target.value })} maxLength={200} required />
        <TextField label="First user's email" type="email" value={f.userEmail} onChange={(e) => set({ userEmail: e.target.value })} maxLength={254} required error={field === "userEmail" ? message(err) : undefined} />
        <Toggle
          label="Send welcome email"
          checked={f.sendWelcome && connected}
          disabled={!connected}
          onChange={(v) => set({ sendWelcome: v })}
          description={connected ? "Your FlexFreight account is ready: a button to set their password, valid 7 days." : welcomeOffReason(status.data)}
        />
        <CustomerEmailFix status={status.data} />
        {err && field !== "userEmail" ? (
          <p className="ffa-warn ffa-small" role="alert">{message(err)}</p>
        ) : null}
        {existing.length ? (
          <ul className="ffa-plainlist ffa-small" aria-label="Existing customers with this email">
            {existing.map((c) => (
              <li key={c.id}>
                <a href={`/customers/${c.id}`} onClick={(e) => { e.preventDefault(); onClose(); void navigate(`/customers/${c.id}?tab=users`); }}>Open {c.name}</a>
              </li>
            ))}
          </ul>
        ) : null}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/** Users tab > Add user. */
export function AddUserDialog({ orgId, orgName, onClose }: { orgId: string; orgName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const status = useEmailStatus();
  const grantOwner = canGrantOwner(useConsoleMe());
  const [f, setF] = useState({ name: "", email: "", role: "admin", sendWelcome: true });
  const [done, setDone] = useState<AddUserOut | null>(null);
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch });
  const add = useMutation({
    mutationFn: () => api<AddUserOut>(`/v1/console/customers/${orgId}/users`, { method: "POST", body: { name: f.name.trim(), email: f.email.trim(), role: f.role, sendWelcome: f.sendWelcome } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "customer", orgId] });
      if (r.link) setDone(r);
      else {
        notify.success("User added", r.emailSent ? `Email sent to ${r.user.email}.` : undefined);
        onClose();
      }
    },
  });
  if (done?.link) return <OneTimeLinkDialog link={done.link} who={done.user.email} connected={done.emailConnected} emailFailed={f.sendWelcome && done.emailConnected && !done.emailSent} onClose={onClose} />;
  const connected = status.data?.connected ?? false;
  const valid = f.name.trim() !== "" && EMAIL_RE.test(f.email.trim());
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Add a user to ${orgName}`}
      description="New addresses get a one-time link to choose a password. Someone who already has a portal account is added and told to sign in."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={add.isPending} onClick={() => add.mutate()}>Add user</Button>
        </>
      }
    >
      <form className="ffa-form" aria-label="New user" onSubmit={(e) => { e.preventDefault(); if (valid) add.mutate(); }}>
        <TextField label="Name" value={f.name} onChange={(e) => set({ name: e.target.value })} maxLength={200} required />
        <TextField label="Email" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} maxLength={254} required error={fieldOf(add.error) === "email" ? message(add.error) : undefined} />
        <Select label="Role" value={f.role} onChange={(v) => set({ role: v })} options={roleOptions(grantOwner)} hint={grantOwner ? undefined : OWNER_GRANT_HINT} />
        <p className="ffa-muted ffa-small">The company&apos;s owners get an email saying FlexFreight staff added this person.</p>
        <Toggle label="Send welcome email" checked={f.sendWelcome && connected} disabled={!connected} onChange={(v) => set({ sendWelcome: v })} description={connected ? undefined : welcomeOffReason(status.data)} />
        <CustomerEmailFix status={status.data} />
        {add.error && fieldOf(add.error) !== "email" ? <p className="ffa-warn ffa-small" role="alert">{message(add.error)}</p> : null}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/**
 * Users tab row actions: resend welcome / send reset, copy a link, deactivate (or remove from this company) or
 * reactivate. Copy link appears only when email is off and to owner or admin with a strong factor: a copied link signs
 * in as that person, so the API refuses it otherwise.
 */
export function UserActions({ orgId, user, email }: { orgId: string; user: CustomerUser; email: EmailStatus | undefined }) {
  const qc = useQueryClient();
  const me = useConsoleMe();
  const { confirm, dialog } = useConfirm();
  const others = user.otherCustomers ?? 0;
  const [link, setLink] = useState<OneTimeLink | null>(null);
  const base = `/v1/console/customers/${orgId}/users/${user.id}`;
  const label = user.name || user.email;
  const send = useMutation({
    mutationFn: () => api<{ sent: true; expiresAt: string }>(`${base}/${user.invitePending ? "welcome" : "password-reset"}`, { method: "POST" }),
    onSuccess: () => notify.success(user.invitePending ? "Welcome email sent" : "Password reset sent", `To ${user.email}. Any older link stopped working.`),
    onError: (e) => notify.error("Not sent", message(e)),
  });
  const copy = useMutation({
    mutationFn: () => api<OneTimeLink>(`${base}/link`, { method: "POST", body: { kind: user.invitePending ? "invite" : "reset" } }),
    onSuccess: (r) => setLink(r),
    onError: (e) => notify.error("No link made", message(e)),
  });
  const toggle = useMutation({
    mutationFn: (disabled: boolean) => api<{ disabled: boolean; removed: boolean; otherCustomers: number }>(base, { method: "PATCH", body: { disabled } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "customer", orgId] });
      if (r.removed) notify.success(`${label} removed from this company`, "Their sign-in still works for their other companies.");
      else notify.success(r.disabled ? `${label} deactivated` : `${label} reactivated`, r.disabled ? "Signed out everywhere; open links stopped working." : undefined);
    },
    onError: (e) => notify.error("Not changed", message(e)),
  });
  const connected = email?.connected ?? false;
  const canCopy = !connected && canGrantOwner(me);
  const plural = others === 1 ? "company" : "companies";
  if (user.disabled) {
    return (
      <div className="ffa-rowactions">
        <Button size="sm" variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate(false)}>Reactivate</Button>
      </div>
    );
  }
  return (
    <div className="ffa-rowactions">
      <Button size="sm" disabled={!connected} loading={send.isPending} onClick={() => send.mutate()} title={connected ? undefined : EMAIL_NOT_CONNECTED}>
        {user.invitePending ? "Resend welcome" : "Send password reset"}
      </Button>
      {canCopy ? <Button size="sm" variant="ghost" loading={copy.isPending} onClick={() => copy.mutate()}>Copy link</Button> : null}
      <Button
        size="sm"
        variant="ghost"
        loading={toggle.isPending}
        onClick={async () => {
          const ok = await confirm(
            others > 0
              ? {
                  title: `Remove ${label} from this company?`,
                  body: `They lose access to this company at once. They also belong to ${others} other customer ${plural}, so their sign-in keeps working there.`,
                  confirmLabel: "Remove from this company",
                  danger: true,
                }
              : {
                  title: `Deactivate ${label}'s login?`,
                  body: "They are signed out at once and can't sign in to the FlexFreight portal until reactivated. Their open welcome or reset links stop working.",
                  confirmLabel: "Deactivate login",
                  danger: true,
                },
          );
          if (ok) toggle.mutate(true);
        }}
      >
        {others > 0 ? "Remove from this company" : "Deactivate login"}
      </Button>
      {link ? <OneTimeLinkDialog link={link} who={user.email} connected={connected} onClose={() => setLink(null)} /> : null}
      {dialog}
    </div>
  );
}
